import { SearchInput } from '@/shared/components/Filters';
import { Loader } from '@/shared/components/common/Loader';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useShopifyList } from '@/shared/hooks/useShopifyList';
import type { UserPermission } from '@/shared/types/permissions';
import type { FilterConfig, ShopifyFilters, ShopifyListColumn } from '@/shared/types/shopify';
import { scrollMainContentToTop } from '@/shared/utils/scrollMainContent';
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CFormSelect,
  CRow,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react';
import React from 'react';
import { DEFAULT_PAGE_SIZE_OPTIONS } from './constants';

interface ShopifyListProps<T, F extends ShopifyFilters> {
  title: string;
  endpoint: string;
  columns: ShopifyListColumn<T>[];
  defaultFilters: F;
  filterConfigs: FilterConfig[];
  renderCell?: (item: T, column: ShopifyListColumn<T>) => React.ReactNode;
  getRowKey: (item: T) => React.Key;
  pageSizeOptions?: number[];
  requiredViewPermission?: UserPermission;
}

export const ShopifyList = <T, F extends ShopifyFilters>({
  title,
  endpoint,
  columns,
  defaultFilters,
  filterConfigs,
  renderCell,
  getRowKey,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  requiredViewPermission,
}: ShopifyListProps<T, F>) => {
  const { hasPermission } = usePermissions();

  const canView = !requiredViewPermission || hasPermission(requiredViewPermission);
  const {
    items,
    loading,
    error,
    pageInfo,
    filters,
    handleNextPage,
    handlePreviousPage,
    handlePageSizeChange,
    handleFiltersChange,
    handleClearFilters,
    isFiltersEmpty,
  } = useShopifyList<T, F>(endpoint, defaultFilters);
  const [searchResetCounters, setSearchResetCounters] = React.useState<Record<string, number>>({});

  const scrollAfterPagination = () => {
    scrollMainContentToTop();
  };

  const handlePageSizeSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    scrollAfterPagination();
    handlePageSizeChange(Number(event.target.value));
  };

  const handlePreviousPageClick = () => {
    handlePreviousPage();
    requestAnimationFrame(scrollAfterPagination);
  };

  const handleNextPageClick = () => {
    handleNextPage();
    requestAnimationFrame(scrollAfterPagination);
  };

  const handleSearch = (key: string, value: string) => {
    setSearchResetCounters((current) =>
      Object.fromEntries(
        filterConfigs.map((config) => [
          config.key,
          config.key === key ? current[config.key] || 0 : (current[config.key] || 0) + 1,
        ]),
      ),
    );
    const searchFilters = Object.fromEntries(
      filterConfigs.map((config) => [config.key, config.key === key ? value : '']),
    );
    handleFiltersChange(searchFilters as Partial<F>);
  };

  const handleClear = () => {
    setSearchResetCounters((current) =>
      Object.fromEntries(
        filterConfigs.map((config) => [config.key, (current[config.key] || 0) + 1]),
      ),
    );
    handleClearFilters();
  };

  const defaultRenderCell = (item: T, column: ShopifyListColumn<T>) => {
    if (column.render) {
      return column.render(item);
    }
    const value = item[column.key];
    return value ? String(value) : '-';
  };

  const cellRenderer = renderCell || defaultRenderCell;

  if (!canView) {
    return (
      <CCard>
        <CCardBody>
          <CAlert color="danger">You don't have permission to view this content</CAlert>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <div>
      <CCard>
        <CCardHeader>
          <h4 className="mb-0">{title}</h4>
        </CCardHeader>

        <CCardBody>
          <Loader isLoading={loading} />

          {error && (
            <CAlert color="danger" dismissible>
              {error}
            </CAlert>
          )}

          <CRow className="mb-3">
            {filterConfigs.map((config) => (
              <CCol key={config.key} md={filterConfigs.length > 1 ? 4 : 8}>
                <SearchInput
                  key={`${config.key}-${searchResetCounters[config.key] || 0}`}
                  value={filters[config.key] || ''}
                  onChange={(value) => handleSearch(config.key, value)}
                  placeholder={config.placeholder}
                />
              </CCol>
            ))}
            <CCol md={4}>
              <CButton color="secondary" onClick={handleClear} disabled={isFiltersEmpty()}>
                Clear Filters
              </CButton>
            </CCol>
          </CRow>

          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => (
                  <CTableHeaderCell key={String(column.key)}>{column.header}</CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {!items || items.length === 0 ? (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center">
                    {loading ? 'Loading...' : 'No items found'}
                  </CTableDataCell>
                </CTableRow>
              ) : (
                items.map((item) => (
                  <CTableRow key={getRowKey(item)}>
                    {columns.map((column) => (
                      <CTableDataCell key={String(column.key)}>
                        {cellRenderer(item, column)}
                      </CTableDataCell>
                    ))}
                  </CTableRow>
                ))
              )}
            </CTableBody>
          </CTable>

          <CRow className="mt-3">
            <CCol>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <CFormSelect
                    value={filters.pageSize || 25}
                    onChange={handlePageSizeSelectChange}
                    style={{ width: 'auto' }}
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option} value={option}>
                        {option} per page
                      </option>
                    ))}
                  </CFormSelect>
                </div>
                {(pageInfo.hasNextPage || pageInfo.hasPreviousPage) && (
                  <div>
                    <CButton
                      color="primary"
                      onClick={handlePreviousPageClick}
                      disabled={!pageInfo.hasPreviousPage || loading}
                      className="me-2"
                    >
                      Previous
                    </CButton>
                    <CButton
                      color="primary"
                      onClick={handleNextPageClick}
                      disabled={!pageInfo.hasNextPage || loading}
                    >
                      Next
                    </CButton>
                  </div>
                )}
              </div>
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>
    </div>
  );
};
